import {createAsyncThunk, createSelector, createSlice} from "@reduxjs/toolkit";
import Swal from "sweetalert2";

import {fetchData, fetchDataNew, fetchList, fetchListNew} from "../hook/axios.hook";
import i18n from "../i18n";
import {checkEmptyFields} from "../utilities/checkEmptyFields";
import {toaster} from "../utilities/hooks/toaster";

import {selectCurrentUser, updateUser} from "./UserSlice";

export const getError = error => {
  toaster.error(error?.message || String(error));
};
export const getInfo = text => {
  toaster.info(text);
};

const initialState = {
  persons: [],
  clients: [],
  designers: [],
  leads: [],
  lead: null,

  users: [],
  userPositions: [],
  positions: [],
  routes: [],
  currentPerson: null,
};

export const addNewPerson = createAsyncThunk("person/addNewPerson", async person => {
  return fetchData("/persona/add", "post", checkEmptyFields(person));
});
export const updatePerson = createAsyncThunk("person/updatePerson", async ({personaId, body}) => {
  return fetchData(`/persona/${personaId}`, "patch", body);
});
export const fetchPersons = createAsyncThunk("person/fetchPersons", async () => {
  return fetchData("/persona/getAll", "get");
});
export const fetchUsers = createAsyncThunk("person/fetchUsers", async params => {
  return fetchData("/users/getAll", "get", params);
});

export const fetchOrgStructures = createAsyncThunk("person/fetchOrgStructures", async () => {
  return fetchData("/orgStructure/getAll", "get");
});

export const fetchClients = createAsyncThunk("person/fetchClients", async () => fetchList("/clients/getAll", "get"));

export const registrClient = createAsyncThunk("person/registrClient", async client => {
  return fetchData("/clients/add", "post", client);
});

export const registrUser = createAsyncThunk("person/registrUser", async user => {
  return fetchData("/users/add", "post", user);
});

//*** Leads */
export const fetchAllLeads = createAsyncThunk("person/fetchAllLeads", async () => {
  return fetchData("/leads", "get");
});
export const fetchLead = createAsyncThunk("person/fetchLead", async ({id}) => {
  return fetchData(`/leads/${id}`, "get");
});
export const addNewLead = createAsyncThunk("person/addNewLead", async body => {
  return fetchData(`/leads/create`, "post", body);
});
export const updateLead = createAsyncThunk("person/updateLead", async ({id, body}) => {
  if (!id) return;
  return fetchData(`/leads/${id}`, "patch", body);
});
export const deleteLead = createAsyncThunk("person/deleteLead", async ({id}) => {
  if (!id) return;
  return fetchData(`/leads/${id}`, "delete");
});

//*** Positions */
export const fetchAllPositions = createAsyncThunk("hr/fetchAllPositions", async (_, {signal, rejectWithValue}) => {
  return fetchListNew("/position/getAll", "get", {signal, rejectWithValue});
});
export const addNewPosition = createAsyncThunk("hr/addNewPosition", async (body, {signal, rejectWithValue}) => {
  return fetchDataNew("/position", "post", {body, signal, rejectWithValue});
});
export const updatePosition = createAsyncThunk(
  "hr/updatePosition",
  async ({positionId, body}, {signal, rejectWithValue}) => {
    if (!positionId) return;
    return fetchDataNew(`/position/${positionId}`, "patch", {body, signal, rejectWithValue});
  },
);
export const setPositionRoutes = createAsyncThunk(
  "hr/setPositionRoutes",
  async ({positionId, body}, {signal, rejectWithValue}) => {
    if (!positionId) return;
    return fetchDataNew(`/position/${positionId}/routes`, "patch", {body, signal, rejectWithValue});
  },
);
export const deletePosition = createAsyncThunk("hr/deletePosition", async ({positionId}, {signal, rejectWithValue}) => {
  if (!positionId) return;
  return fetchDataNew(`/position/${positionId}`, "delete", {signal, rejectWithValue});
});

//*** Routes */
export const fetchAllRoutes = createAsyncThunk("hr/fetchAllRoutes", async (_, {signal, rejectWithValue}) => {
  return fetchListNew("/routes/getAll", "get", {signal, rejectWithValue});
});
export const addNewRoute = createAsyncThunk("hr/addNewRoute", async (body, {signal, rejectWithValue}) => {
  return fetchDataNew("/routes", "post", {body, signal, rejectWithValue});
});
export const updateRoute = createAsyncThunk("hr/updateRoute", async ({routeId, body}, {signal, rejectWithValue}) => {
  if (!routeId) return;
  return fetchDataNew(`/routes/${routeId}`, "patch", {body, signal, rejectWithValue});
});
export const deleteRoute = createAsyncThunk("hr/deleteRoute", async ({routeId}, {signal, rejectWithValue}) => {
  if (!routeId) return;
  return fetchDataNew(`/routes/${routeId}`, "delete", {signal, rejectWithValue});
});

const personSlice = createSlice({
  name: "person",
  initialState,
  reducers: {
    personsFetched: (state, {payload}) => {
      state.persons = payload;
    },
    usersFetched: (state, {payload}) => {
      state.users = payload;
    },
    clientsFetched: (state, {payload}) => {
      state.clients = payload;
    },
    userPositionsFetched: (state, {payload}) => {
      state.userPositions = payload.map(item => {
        return {value: item?.positionId, title: item?.[i18n.language === "en" ? "nameEng" : "nameUkr"], ...item};
      });
    },
    addCurrentPerson: (state, {payload}) => {
      state.currentPerson = payload;
    },
    clearLead: state => {
      state.lead = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(addNewPerson.fulfilled, (state, {payload}) => {
        var newPers = payload.persona;
        state.persons.unshift(newPers);
      })
      .addCase(updatePerson.fulfilled, (state, {payload}) => {
        const updatedPerson = payload?.persona || payload;
        if (!updatedPerson?.personaId) return;

        state.persons = state.persons.map(person =>
          person?.personaId === updatedPerson.personaId
            ? {
                ...person,
                ...updatedPerson,
                creator: updatedPerson?.creator ?? person?.creator,
                createdBy: updatedPerson?.createdBy ?? person?.createdBy,
              }
            : person,
        );
      })
      .addCase(fetchPersons.fulfilled, (state, {payload}) => {
        state.persons = payload;
        // .map(persona=>{return {id: persona.CA_PERSONA_ID, name: persona.CA_FULL_NAME}})
      })
      .addCase(fetchUsers.fulfilled, (state, {payload}) => {
        state.users = payload;
      })
      .addCase(fetchOrgStructures.fulfilled, (state, {payload}) => {
        state.orgStructures = payload;
      })
      .addCase(fetchClients.fulfilled, (state, {payload}) => {
        state.clients = payload;
      })
      .addCase(registrClient.fulfilled, (state, {payload}) => {
        state.clients.unshift(payload.client);
        Swal.fire({
          title: `Клієнта успішно зареєстровано: \n${payload.client.clientNumber}`,
          // text: `<span>'CLIENT IN: <b>${payload.client.clientNumber}</b>'</span>`,
          icon: "info",
          confirmButtonText: "Ок",
        });
      })
      // .addCase(registrClient.rejected, (state, {error}) => {
      //     getError(error)
      // })
      .addCase(registrUser.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.users.unshift(payload.user);
        Swal.fire({
          title: `Користувача ${payload.user.name} успішно зареєстровано`,
          text: `Логін: ${payload.user.login}`,
          icon: "info",
          confirmButtonText: "Ок",
        });
      })

      //*** Leads */
      .addCase(fetchAllLeads.fulfilled, (state, {payload}) => {
        state.leads = payload;
      })
      .addCase(fetchLead.fulfilled, (state, {payload}) => {
        state.lead = payload;
      })
      .addCase(addNewLead.fulfilled, (state, {payload}) => {
        state.lead = payload.lead;
        state.leads = [payload.lead, ...state.leads];
      })
      .addCase(updateLead.fulfilled, (state, {payload, meta}) => {
        state.lead = payload.lead;
        state.leads = state.leads.map(lead => (lead.leadId === meta.arg.id ? payload.lead : lead));
      })
      .addCase(deleteLead.fulfilled, (state, {meta}) => {
        state.lead = null;
        state.leads = state.leads.filter(lead => lead.leadId !== meta.arg.id);
      })
      // .addCase(registrUser.rejected, (state, {error}) => {
      //     getError(error)
      // })

      //*** Positions */
      .addCase(fetchAllPositions.fulfilled, (state, {payload}) => {
        state.positions = payload;
      })
      .addCase(addNewPosition.fulfilled, (state, {payload}) => {
        state.positions = [...(state.positions || []), payload];
      })
      .addCase(updatePosition.fulfilled, (state, {payload}) => {
        state.positions = Array.isArray(state.positions)
          ? state.positions.map(position => (position.positionId === payload?.positionId ? payload : position))
          : [];
      })
      .addCase(setPositionRoutes.fulfilled, (state, {payload}) => {
        state.positions = Array.isArray(state.positions)
          ? state.positions.map(position => (position.positionId === payload?.positionId ? payload : position))
          : [];
      })
      .addCase(deletePosition.fulfilled, (state, {meta}) => {
        state.positions = Array.isArray(state.positions)
          ? state.positions.filter(position => position.positionId !== meta.arg?.positionId)
          : [];
      })

      .addCase(updateUser.fulfilled, (state, {payload}) => {
        state.users = Array.isArray(state.users)
          ? state.users.map(user => (user.userId === payload?.userId ? {...user, ...payload} : user))
          : [];
      })

      //*** Routes */
      .addCase(fetchAllRoutes.fulfilled, (state, {payload}) => {
        state.routes = payload;
      })
      .addCase(addNewRoute.fulfilled, (state, {payload}) => {
        state.routes = [...(state.routes || []), payload];
      })
      .addCase(updateRoute.fulfilled, (state, {payload}) => {
        state.routes = Array.isArray(state.routes)
          ? state.routes.map(route => (route.routeId === payload?.routeId ? payload : route))
          : [];
      })
      .addCase(deleteRoute.fulfilled, (state, {meta}) => {
        state.routes = Array.isArray(state.routes)
          ? state.routes.filter(route => route.routeId !== meta.arg?.routeId)
          : [];
      })

      .addDefaultCase(() => {});
  },
});

const {actions, reducer} = personSlice;

export default reducer;

export const {
  personsFetched,
  usersFetched,
  clientsFetched,
  addUser,
  userPositionsFetched,
  addCurrentPerson,
  clearLead,
} = actions;

export const getUsers = state => state.person.users;
export const selectAllUsers = state => state.person.users;
export const getPersons = state => state.person.persons;
export const getClients = state => state.person.clients;
export const selectAllClients = state => state.person.clients;
export const getUsersFull = state => state.person.users;
export const selectAllLeads = state => state.person.leads;
export const selectLead = state => state.person.lead;
export const selectAllPositions = state => state.person.positions;
export const selectAllRoutes = state => state.person.routes;

export const selectCurrentRoutePermissions = createSelector(
  [selectCurrentUser, (_, pathname) => pathname],
  (user, pathname) => {
    const routes = user?.position?.routes;

    if (!Array.isArray(routes) || !pathname) {
      return null;
    }

    const route = routes.find(({routeUrl}) => pathname.includes(routeUrl?.slice(0, -2)));

    const isTopRoute = !pathname.includes("/");

    return route?.settings?.permissions
      ?.find(([routeUrl]) => (isTopRoute ? routeUrl === "/" : pathname.includes(routeUrl)))
      ?.at(1);
  },
);
